import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { PaymentContext, type PaymentContextValue } from "./PaymentContext";
import { ConfigContext } from "@/contexts/ConfigProvider";
import { getDriver } from "./driverRegistry";
import { createPaymentSession } from "./paymentService";
import type {
  CreateSessionInput,
  EnabledMethods,
  PaymentSession,
} from "./types";

// Auto-register every gateway driver: each drivers/<gateway>/register.ts
// self-registers on import. Adding a driver needs no change here.
import.meta.glob("./drivers/*/register.ts", { eager: true });

const DEFAULT_ENABLED: EnabledMethods = {
  card: true,
  applePay: false,
  googlePay: false,
  paypal: false,
};

export function PaymentProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const [session, setSession] = useState<PaymentSession | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // The active gateway is configured globally on the backend and surfaced in the
  // form config (like `footer`). Reading it here lets us mount the gateway Driver
  // from the first render — before any session — so the subtree never remounts
  // when `session` transitions null -> populated. Read softly so the component
  // still works in unit tests rendered without a ConfigProvider.
  const config = useContext(ConfigContext);

  // Monotonic sequence of createSession calls: a slow response that lost the
  // race to a newer request must not clobber the newer session — the charge
  // follows the session amount, so a stale session means a stale charge (#1065).
  const requestSeqRef = useRef(0);
  const sessionRef = useRef<PaymentSession | null>(null);

  const createSession = useCallback(
    async (input: CreateSessionInput, signal?: AbortSignal) => {
      const seq = ++requestSeqRef.current;
      const startedAt = Date.now();
      setLoading(true);
      setError(null);
      try {
        // Keep the one-argument call when no deadline applies, so callers
        // without a signal keep their existing request shape.
        const response = signal
          ? await createPaymentSession(input, signal)
          : await createPaymentSession(input);
        const next = {
          ...response,
          // Use the server's remaining lifetime on the client's clock. Starting
          // before the request is conservative about network time. Older APIs
          // omit expires_in and use the existing 30-minute lifetime.
          expires_at: new Date(
            startedAt + (response.expires_in ?? 1800) * 1000,
          ).toISOString(),
        };
        if (seq === requestSeqRef.current) {
          // Recovery may run in a submit handler captured before this refresh.
          sessionRef.current = next;
          setSession(next);
          return next;
        }
      } catch (e) {
        if (seq === requestSeqRef.current) {
          setError(e instanceof Error ? e : new Error(String(e)));
          throw e;
        }
        // Like a stale success, a stale failure must resolve without a session.
        // Callers must not recover by confirming an older session after a newer
        // request has taken ownership (possibly for a different amount).
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const recreateSession = useCallback(
    async (input: CreateSessionInput, signal?: AbortSignal) => {
      const oldId = sessionRef.current?.session_id;
      return createSession({ ...input, old_session_id: oldId }, signal);
    },
    [createSession],
  );

  const value: PaymentContextValue = useMemo(
    () => ({
      session,
      isLoading,
      error,
      enabledMethods: DEFAULT_ENABLED,
      createSession,
      recreateSession,
    }),
    [session, isLoading, error, createSession, recreateSession],
  );

  // Config first (known before the session); fall back to the session's gateway
  // if config doesn't carry it (misconfig, or a test without a ConfigProvider).
  const gateway = config?.payment?.gateway ?? session?.gateway;
  const Driver = gateway ? getDriver(gateway).Provider : null;

  return (
    <PaymentContext.Provider value={value}>
      {Driver ? <Driver session={session}>{children}</Driver> : children}
    </PaymentContext.Provider>
  );
}
